import { Injectable } from '@angular/core';
import { environment as env } from '@src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { InboxRepository } from './inbox.repository';
import {
  InboxConversationDto,
  InboxConversationDetailDto,
  InboxAttachmentDto,
  InboxMessageDto,
  ReservationActionStateDto,
} from '@src/app/core/models/conversation.model';
import { TicketModel } from '@src/app/core/models/ticket.model';

/**
 * AI concierge reply suggestion returned by the backend (`agent-reply`).
 * One bubble per intended message — rendered as editable drafts.
 */
export interface AgentReplySuggestion {
  kind: 'social' | 'clarify' | 'substantive' | 'escalate' | 'noop';
  bubbles: string[];
  quickReplies: string[];
  language: string | null;
  /** Decision signals (copilot/autopilot). */
  confidence?: number | null;
  /** Only when `kind === 'escalate'`: surfaced to the host team, NOT the guest. */
  escalationReason?: string | null;
  escalationSummary?: string | null;
  /** Id of the AI generation behind this suggestion — echo it back on send so
   * the sent message links to the draft it came from. */
  aiActivityId?: string;
}

/** One apartment offered as a property-filter option (thin projection). */
export interface InboxPropertyOptionDto {
  _id: string;
  name: string;
  nickName?: string;
}

/** Per-bucket triage counts for the inbox chips (server-computed). */
export interface InboxTriageCountsDto {
  needsHuman: number;
  needsReply: number;
  review: number;
  answeredByAi: number;
}

/** One source the AI's answer rested on (the "responded from: …" evidence). */
export interface AiActivitySourceDto {
  ref: string;
  type: string;
  snippet: string;
}

/** A sub-request the AI could NOT corroborate (the "couldn't answer: …"). */
export interface AiActivityUnresolvedDto {
  domain: string;
  request: string;
}

/**
 * One AI generation for a conversation — a copilot draft (✨) or an autopilot
 * decision (shadow / scheduled / escalated) — with its evidence and the guest
 * messages it answered. Rendered in the inbox "Copilot" tab.
 */
export interface AiActivityDto {
  _id: string;
  conversation: string;
  source: 'copilot' | 'autopilot';
  action?: 'auto' | 'escalate' | 'draft' | 'team_answer' | 'eta_recorded';
  reason?: string;
  confidence?: number | null;
  kind?: string;
  body?: string;
  /** Guest-facing text the generation produced — present even on `escalate`
   * (where `body` is the escalation summary, not the draft). */
  draft?: string;
  /** Set when a human edited this generation before sending: what was sent. */
  editedBody?: string;
  editedAt?: string;
  /** Set when the held auto-reply was discarded before sending (review-window
   * ✕, a human reply superseding it, or a newer generation replacing it) — the
   * row must not read "answered automatically". */
  cancelledAt?: string;
  /** Ticket the agent's turn actually filed (trace-extracted, never model
   * prose): the feed shows work the AI created for the team. */
  ticket?: { id: string; action: 'created' | 'updated'; subject?: string };
  /** ETA/ETD the turn recorded on the booking (trace-extracted). */
  estimatedTimes?: { arrivalTime?: string; departureTime?: string };
  sources?: AiActivitySourceDto[];
  unresolved?: AiActivityUnresolvedDto[];
  sensitive?: boolean;
  /** The guest messages this generation answered (per-conversation `seq`). */
  answeredSeqFrom?: number;
  answeredSeqTo?: number;
  answeredMessageIds?: string[];
  /** Human-readable preview of the guest text that was answered. */
  answeredPreview?: string;
  createdAt?: string;
}

/** One page of AI activity (newest first). `nextCursor` is null when there is
 * no older page — feed it back to `getAiActivity` to load the next page. */
export interface AiActivityPage {
  items: AiActivityDto[];
  nextCursor: string | null;
}

/**
 * Service for managing conversations.
 */
@Injectable()
export class InboxService {
  constructor(
    private http: HttpClient,
    private inboxRepository: InboxRepository,
  ) {}

  /**
   * Get the organization's inbox conversations (canonical).
   * @param organizationId Organization ID to filter conversations (required).
   */
  /**
   * One page of the conversation list (keyset pagination). Pass the returned
   * `nextCursor` to fetch the next page (null = no more).
   * - First page (`replace !== false`, no cursor) → REPLACES the store (fresh
   *   load / workspace switch).
   * - `replace: false` (refresh, language change) or a `cursor` (infinite
   *   scroll) → MERGES into the store, so the scrolled tail isn't lost.
   */
  getPage(
    organizationId: string,
    opts: {
      cursor?: string;
      limit?: number;
      lang?: string;
      replace?: boolean;
      status?: string;
      sort?: string;
      order?: string;
      channels?: string[];
      stayPhases?: string[];
      signals?: string[];
      triage?: string;
      assignee?: string;
      team?: string;
      properties?: string[];
    } = {},
  ): Observable<{ items: InboxConversationDto[]; nextCursor: string | null }> {
    const replacing = !opts.cursor && opts.replace !== false;
    if (replacing) this.inboxRepository.setLoading(true);

    let params = new HttpParams();
    if (opts.lang) params = params.set('lang', opts.lang);
    if (opts.cursor) params = params.set('cursor', opts.cursor);
    if (opts.limit) params = params.set('limit', String(opts.limit));
    if (opts.status) params = params.set('status', opts.status);
    if (opts.sort) params = params.set('sort', opts.sort);
    if (opts.order) params = params.set('order', opts.order);
    if (opts.channels?.length)
      params = params.set('channels', opts.channels.join(','));
    if (opts.stayPhases?.length)
      params = params.set('stayPhases', opts.stayPhases.join(','));
    if (opts.signals?.length)
      params = params.set('signals', opts.signals.join(','));
    if (opts.triage) params = params.set('triage', opts.triage);
    if (opts.assignee) params = params.set('assignee', opts.assignee);
    if (opts.team) params = params.set('team', opts.team);
    if (opts.properties?.length)
      params = params.set('properties', opts.properties.join(','));

    return this.http
      .get<{
        items: InboxConversationDto[];
        nextCursor: string | null;
      }>(
        `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations`,
        { params },
      )
      .pipe(
        tap((res) => {
          // Pass the org so the store can reject a stale response that resolves
          // after the user already switched workspace (no cross-org mixing).
          if (replacing) {
            this.inboxRepository.setConversations(res.items, organizationId);
          } else {
            this.inboxRepository.upsertConversations(res.items, organizationId);
          }
        }),
        finalize(() => {
          if (replacing) this.inboxRepository.setLoading(false);
        }),
        catchError((error) => {
          this.inboxRepository.setError(error);
          return of({ items: [], nextCursor: null });
        }),
      );
  }

  /** Server-side inbox search (guest/property name, preview, thread id). */
  searchConversations(
    organizationId: string,
    query: string,
    lang?: string,
  ): Observable<InboxConversationDto[]> {
    let params = new HttpParams().set('q', query);
    if (lang) params = params.set('lang', lang);
    return this.http
      .get<
        InboxConversationDto[]
      >(`${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/search`, { params })
      .pipe(catchError(() => of([])));
  }

  /** Total open (non-archived) conversations — for the list badge. */
  getOpenCount(organizationId: string): Observable<number> {
    return this.http
      .get<{
        open: number;
      }>(
        `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/count`,
      )
      .pipe(
        map((r) => r.open),
        catchError(() => of(0)),
      );
  }

  /** The org's apartments, thinned to what the property filter needs. */
  getPropertyOptions(
    organizationId: string,
  ): Observable<InboxPropertyOptionDto[]> {
    return this.http
      .get<
        { _id: string; name?: string; nickName?: string }[]
      >(`${env.api.serverUrl}/apartments/by-organization/${organizationId}`)
      .pipe(
        map((apartments) =>
          apartments.map((a) => ({
            _id: a._id,
            name: a.name ?? '',
            nickName: a.nickName,
          })),
        ),
        catchError(() => of([])),
      );
  }

  /** Per-bucket triage counts for the inbox chips, honoring active filters. */
  getTriageCounts(
    organizationId: string,
    filters: {
      channels?: string[];
      stayPhases?: string[];
      signals?: string[];
      assignee?: string;
      team?: string;
      properties?: string[];
    } = {},
  ): Observable<InboxTriageCountsDto> {
    let params = new HttpParams();
    if (filters.channels?.length)
      params = params.set('channels', filters.channels.join(','));
    if (filters.stayPhases?.length)
      params = params.set('stayPhases', filters.stayPhases.join(','));
    if (filters.signals?.length)
      params = params.set('signals', filters.signals.join(','));
    if (filters.assignee) params = params.set('assignee', filters.assignee);
    if (filters.team) params = params.set('team', filters.team);
    if (filters.properties?.length)
      params = params.set('properties', filters.properties.join(','));
    return this.http
      .get<InboxTriageCountsDto>(
        `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/triage-counts`,
        { params },
      )
      .pipe(
        catchError(() =>
          of({ needsHuman: 0, needsReply: 0, review: 0, answeredByAi: 0 }),
        ),
      );
  }

  /**
   * Pull real conversations from the PMS into the canonical inbox (superadmin).
   */
  syncConversations(
    organizationId: string,
  ): Observable<{ threads: number; messages: number }> {
    return this.http.post<{ threads: number; messages: number }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/sync`,
      {},
    );
  }

  /**
   * Get a single conversation with its messages (canonical inbox detail).
   */
  getConversation(
    organizationId: string,
    conversationId: string,
    afterSeq?: number,
  ): Observable<InboxConversationDetailDto> {
    const base = `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}`;
    const url = afterSeq !== undefined ? `${base}?afterSeq=${afterSeq}` : base;
    return this.http.get<InboxConversationDetailDto>(url);
  }

  /**
   * Translate a conversation's messages into a target language (on-read), caching
   * the result per message server-side. `targetLang` defaults to the requesting
   * user's preferred language. Returns the (updated) messages so the inbox can
   * render `translations[targetLang]` immediately.
   */
  translateMessages(
    organizationId: string,
    conversationId: string,
    targetLang?: string,
    messageIds?: string[],
  ): Observable<{
    conversationId: string;
    targetLang: string;
    messages: InboxMessageDto[];
  }> {
    return this.http.post<{
      conversationId: string;
      targetLang: string;
      messages: InboxMessageDto[];
    }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/translations`,
      {
        ...(targetLang ? { targetLang } : {}),
        ...(messageIds?.length ? { messageIds } : {}),
      },
    );
  }

  /**
   * Tickets linked to a booking (for the inbox right panel). Isolated call —
   * does NOT populate the global ticket store. Returns [] on error.
   */
  getTicketsForBooking(
    organizationId: string,
    bookingId: string,
    lang?: string,
  ): Observable<TicketModel[]> {
    const params = new HttpParams()
      .set('organizationId', organizationId)
      .set('booking', bookingId)
      .set('page', '1')
      .set('limit', '100')
      .set('lang', lang || '');
    return this.http
      .get<TicketModel[]>(`${env.api.serverUrl}/tickets`, { params })
      .pipe(catchError(() => of([] as TicketModel[])));
  }

  /**
   * Mark a conversation as read (reset unread count). Internal only — does not
   * propagate a read receipt to the PMS.
   */
  markConversationRead(
    organizationId: string,
    conversationId: string,
  ): Observable<{ unreadCount: number }> {
    return this.http.post<{ unreadCount: number }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/read`,
      {},
    );
  }

  /**
   * Bulk mark conversations read/unread (list multi-select). Internal only —
   * does not propagate read receipts to the PMS.
   */
  setConversationsReadState(
    organizationId: string,
    conversationIds: string[],
    read: boolean,
  ): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/read-state`,
      { conversationIds, read },
    );
  }

  /** Pin/unpin a conversation (floats it to the top of the list). */
  setPinned(
    organizationId: string,
    conversationId: string,
    pinned: boolean,
  ): Observable<{ pinned: boolean }> {
    return this.http.post<{ pinned: boolean }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/pin`,
      { pinned },
    );
  }

  /**
   * Assign/unassign a conversation. An ABSENT key leaves the field untouched;
   * an explicit `null` clears it — mirroring the backend patch semantics.
   */
  setAssignment(
    organizationId: string,
    conversationId: string,
    patch: { assignee?: string | null; team?: string | null },
  ): Observable<{ assignee: string | null; team: string | null }> {
    return this.http.patch<{ assignee: string | null; team: string | null }>(
      `${env.api.serverUrl}/organizations/${organizationId}/inbox/conversations/${conversationId}/assignment`,
      patch,
    );
  }

  /** Open or resolve (close) a conversation. */
  setStatus(
    organizationId: string,
    conversationId: string,
    status: 'open' | 'resolved',
  ): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/status`,
      { status },
    );
  }

  /** Approve the pending reservation request → returns the fresh action-state. */
  approveReservation(
    organizationId: string,
    conversationId: string,
  ): Observable<ReservationActionStateDto> {
    return this.http.post<ReservationActionStateDto>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/reservation/approve`,
      {},
    );
  }

  /** Decline the pending reservation request. */
  declineReservation(
    organizationId: string,
    conversationId: string,
    opts?: { reasonCode?: string; message?: string },
  ): Observable<ReservationActionStateDto> {
    return this.http.post<ReservationActionStateDto>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/reservation/decline`,
      opts ?? {},
    );
  }

  /**
   * Edit the booking's estimated arrival/departure time and push it to the PMS.
   * Send property-local "HH:mm" (the PM picks the real local time); the backend
   * converts to the canonical UTC instant. Returns the persisted UTC instants.
   * Omit a field to leave that side unchanged.
   */
  updateEstimatedTimes(
    organizationId: string,
    conversationId: string,
    times: { estimatedArrivalTime?: string; estimatedDepartureTime?: string },
  ): Observable<{
    estimatedArrival?: string;
    estimatedDeparture?: string;
    pmsSynced?: boolean;
  }> {
    return this.http.patch<{
      estimatedArrival?: string;
      estimatedDeparture?: string;
      pmsSynced?: boolean;
    }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/estimated-times`,
      times,
    );
  }

  /** Pre-approve the reservation inquiry. */
  preApproveReservation(
    organizationId: string,
    conversationId: string,
  ): Observable<ReservationActionStateDto> {
    return this.http.post<ReservationActionStateDto>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/reservation/pre-approve`,
      {},
    );
  }

  /**
   * Send a host reply to the guest of a conversation (real outbound via the PMS).
   * The backend gates this behind INBOX_OUTBOUND_ENABLED (403 when off).
   */
  sendReply(
    organizationId: string,
    conversationId: string,
    message: string,
    attachments?: InboxAttachmentDto[],
    /** Attribution when the composer held an AI draft: its source, whether the
     * operator edited it, the verbatim draft they started from, and the id of
     * the generation behind it (for generated-vs-sent analysis). */
    ai?: {
      source?: 'autopilot' | 'copilot';
      edited?: boolean;
      draft?: string | null;
      aiActivityId?: string | null;
    },
    /** Macro provenance when the composer text started from a saved reply:
     * its id + the rendered body as inserted (pre-edit), so the backend
     * stores the inserted-vs-sent pair — sibling of `ai.draft`. */
    savedReply?: {
      savedReplyId: string;
      savedReplyBody?: string | null;
    },
  ): Observable<{ conversationId: string; messageId: string }> {
    return this.http.post<{ conversationId: string; messageId: string }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/reply`,
      {
        message,
        ...(attachments?.length ? { attachments } : {}),
        ...(ai?.source ? { aiSource: ai.source } : {}),
        ...(ai?.edited !== undefined ? { aiEdited: ai.edited } : {}),
        ...(ai?.draft ? { aiDraft: ai.draft } : {}),
        ...(ai?.aiActivityId ? { aiActivityId: ai.aiActivityId } : {}),
        ...(savedReply?.savedReplyId
          ? {
              savedReplyId: savedReply.savedReplyId,
              ...(savedReply.savedReplyBody
                ? { savedReplyBody: savedReply.savedReplyBody }
                : {}),
            }
          : {}),
      },
    );
  }

  /** Retry a previously failed outbound message (re-send to the PMS). Resolves
   * with the new delivery status of that same message. */
  retryReply(
    organizationId: string,
    conversationId: string,
    messageId: string,
  ): Observable<{
    conversationId: string;
    messageId: string;
    status: 'delivered' | 'failed';
  }> {
    return this.http.post<{
      conversationId: string;
      messageId: string;
      status: 'delivered' | 'failed';
    }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/messages/${messageId}/retry`,
      {},
    );
  }

  /**
   * Upload a file to send as a reply attachment (stored durably in GCS). The
   * backend validates it against the provider's outbound-attachment capability.
   * Returns the canonical attachment descriptor to pass to `sendReply`.
   */
  uploadAttachment(
    organizationId: string,
    conversationId: string,
    file: File,
  ): Observable<InboxAttachmentDto> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<InboxAttachmentDto>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/attachments`,
      form,
    );
  }

  /**
   * Import an image from a URL to send as a reply attachment. Dragging a picture
   * from another browser tab gives us a URL rather than the bytes, and the
   * browser can't fetch it itself (cross-origin images rarely allow CORS), so
   * the backend downloads it — with its own SSRF + capability validation.
   */
  importAttachmentFromUrl(
    organizationId: string,
    conversationId: string,
    url: string,
  ): Observable<InboxAttachmentDto> {
    return this.http.post<InboxAttachmentDto>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/attachments/import`,
      { url },
    );
  }

  /** Cancel a pending autopilot auto-reply held in the review window. */
  cancelAutoReply(
    organizationId: string,
    conversationId: string,
  ): Observable<{ cancelled: boolean }> {
    return this.http.post<{ cancelled: boolean }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/cancel-auto-reply`,
      {},
    );
  }

  /** Answer a structured PM question (knowledge-gap card): files the answer as
   * a manager fact and regenerates the guest reply, held for review. */
  answerPmQuestion(
    organizationId: string,
    conversationId: string,
    questionId: string,
    answer: string,
  ): Observable<{ answered: boolean }> {
    return this.http.post<{ answered: boolean }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/pm-questions/${questionId}/answer`,
      { answer },
    );
  }

  /** Skip a structured PM question (no fact written). */
  skipPmQuestion(
    organizationId: string,
    conversationId: string,
    questionId: string,
  ): Observable<{ skipped: boolean }> {
    return this.http.post<{ skipped: boolean }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/pm-questions/${questionId}/skip`,
      {},
    );
  }

  /**
   * Add an internal team note to a conversation (stored in our inbox only —
   * never sent to the guest). Not gated by the outbound flag.
   */
  addNote(
    organizationId: string,
    conversationId: string,
    message: string,
  ): Observable<{ conversationId: string; messageId: string }> {
    return this.http.post<{ conversationId: string; messageId: string }>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/note`,
      { message },
    );
  }

  /**
   * Ask the AI concierge for a reply suggestion to the guest's latest message
   * in a conversation (inbox copilot). The backend derives the message,
   * history and booking context from the stored conversation.
   * @param organizationId Organization that owns the conversation.
   * @param conversationId Conversation to generate a reply for.
   */
  getAgentReply(
    organizationId: string,
    conversationId: string,
  ): Observable<AgentReplySuggestion> {
    return this.http.post<AgentReplySuggestion>(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/agent-reply`,
      {},
    );
  }

  /**
   * List the AI activity (copilot drafts + autopilot decisions) for a
   * conversation, newest first — the data behind the inbox "Copilot" tab.
   * @param organizationId Organization that owns the conversation.
   * @param conversationId Conversation to load activity for.
   * @param cursor Keyset cursor for the next (older) page.
   * @param lang Reader's UI language. The agent writes the escalation summary
   * and every unresolved request in English regardless of who reads them; the
   * backend translates those (and caches per language). Guest-facing text —
   * the draft, a sent reply, the quoted guest preview — is never translated.
   */
  getAiActivity(
    organizationId: string,
    conversationId: string,
    cursor?: string,
    lang?: string,
  ): Observable<AiActivityPage> {
    const url = new URL(
      `${env.api.serverUrl}/pms-integrations/organization/${organizationId}/conversations/${conversationId}/ai-activity`,
    );
    if (cursor) url.searchParams.set('cursor', cursor);
    if (lang) url.searchParams.set('lang', lang);
    return this.http.get<AiActivityPage>(url.toString());
  }
}
